/**
 * viewportFrozenLayers — native per-viewport frozen-layer SET round-trip
 * (DXF group 331) ⇄ semantic layer NAMES (`ViewportEntity.frozenLayers`).
 *
 * AutoCAD stores the layers frozen in a specific viewport as REPEATED group-331
 * soft-pointer handles on the `AcDbViewport` entity, each pointing at a LAYER
 * table record (Autodesk DXF reference, AcDbViewport "Frozen layer object
 * ID/handle"; ezdxf 1.4.3 `entities/viewport.py` — the 331 collector populates
 * `Viewport.frozen_layers`). Our editor-facing model represents the same set
 * SEMANTICALLY as `ViewportEntity.frozenLayers` (layer NAMES) — the VPLAYER
 * freeze/thaw command (command-engine `applyViewportVplayer`) is the single
 * editing source of truth and mutates ONLY the name list.
 *
 * Historically the two representations lived side-by-side without a bridge:
 *   - `frozenLayerHandles` (group 331) round-tripped VERBATIM as raw handles,
 *   - `frozenLayers` (names) round-tripped ONLY through the proprietary
 *     `EDIT_DWG_VP_OVERRIDES` XDATA block (`viewportOverrides.ts`).
 * So an AutoCAD-authored freeze (native 331, no XDATA) was never recognised on
 * import, and an in-editor freeze (name added, no handle) never reached the
 * native 331 carrier on export — it survived only in our own XDATA, invisible
 * to AutoCAD/ezdxf. EXPORT-VP-FROZEN closes that gap: the NAME list is the
 * source of truth and this module is the single place that resolves it against
 * the LAYER table in both directions.
 *
 * DXF layer names are case-insensitive (the rest of the table logic lowercases
 * for comparison), so the name↔handle maps key on the lowercased name. Handles
 * are compared case-insensitively too — DXF hex handles are conventionally
 * upper-case but a foreign writer may emit lower-case.
 */

import {
  VP_OVERRIDES_CONSISTENT_NATIVE_331_VERSION,
  xdataOwnsFrozenLayerSet,
  readViewportOverrides,
  writeViewportOverrides,
} from "./viewportOverrides";

/** Minimal LAYER-table shape this module needs: a name + its DXF record handle. */
export interface FrozenLayerRef {
  name: string;
  handle: string;
}

/** The raw XDATA group shape `readViewportOverrides`/`writeViewportOverrides`
 * read/write — kept structural (not imported from `./model`) for the same
 * leaf-module reason as the rest of this file. */
interface XdataEntry {
  appName: string;
  groups: { code: number; value: string }[];
}

/**
 * Minimal viewport shape the import-side resolution reads/mutates — kept
 * structural so this leaf module never imports the full `./model` graph.
 */
interface FrozenLayerViewport {
  /** Entity id — equals the DXF record handle for imported viewports (used to
   * match against the authoritative-XDATA handle set on import). */
  id?: string;
  type?: string;
  frozenLayers?: string[];
  frozenLayerHandles?: string[];
  /** PRESERVE-SOURCE round-trip carrier — see `model.ts` `ViewportEntity.
   * sourceFrozenLayerHandles` doc. Written by `resolveViewportFrozenLayerNames`. */
  sourceFrozenLayerHandles?: string[];
  /** On-file writer-version marker — see `model.ts` `ViewportEntity.
   * vpOverridesWriterVersion` doc. POSITIVELY identifies a v3+ trustworthy-native
   * viewport independent of the raw-document handle-keyed pre-scan (@codex #514
   * P2 "Don't trust native 331 for unclassified XDATA viewports"). */
  vpOverridesWriterVersion?: number;
  /** Raw EDIT_DWG_VP_OVERRIDES XDATA carrier — read/resynced by the trustworthy-
   * native merge branch below (@codex #514 P2 "Update stale XDATA after
   * trusting native 331"). */
  xdata?: XdataEntry[];
  displayLocked?: boolean;
  viewHeight?: number;
  height?: number;
  vpOverrideScale?: { num: number; denom: number };
  vpOverrideUcsName?: string;
}

/** lower(handle) → canonical layer name. Handle-less layers are skipped. */
function buildHandleToName(
  layers: readonly FrozenLayerRef[],
): Map<string, string> {
  const map = new Map<string, string>();
  for (const layer of layers) {
    // A handle-less layer cannot be a group-331 target; skip it. (Its name can
    // still round-trip via the EDIT_DWG_VP_OVERRIDES XDATA fallback.) Do not let
    // an empty handle collapse multiple layers onto the "" key.
    if (layer.handle) map.set(layer.handle.toLowerCase(), layer.name);
  }
  return map;
}

/** lower(name) → LAYER record handle. Handle-less layers are skipped. */
function buildNameToHandle(
  layers: readonly FrozenLayerRef[],
): Map<string, string> {
  const map = new Map<string, string>();
  for (const layer of layers) {
    if (layer.handle) map.set(layer.name.toLowerCase(), layer.handle);
  }
  return map;
}

/**
 * Reconcile the semantic frozen-layer NAMES with the file's NATIVE group-331
 * LAYER handles. Serves TWO symmetric callers with OPPOSITE trust directions,
 * disambiguated by `options.trustNativeAsAuthoritative`:
 *
 *   - IMPORT (trustNativeAsAuthoritative: true) — reconcile the semantic NAMES
 *     already present on the model (decoded from the EDIT_DWG_VP_OVERRIDES XDATA
 *     at `toDrawingEntity` time) against the native 331, in the regime where the
 *     native 331 is the TRUSTWORTHY freeze source (a FOREIGN AutoCAD file, or a
 *     v3+ consistent-331 writer — see `resolveViewportFrozenLayerNames`). This is
 *     a SET reconciliation, not a one-way union — the native 331 is authoritative
 *     in BOTH directions (PASS A subtracts, PASS B adds; see below).
 *   - EXPORT (trustNativeAsAuthoritative omitted/false) — `dxfBridge.entityConversion
 *     .ts` computes `effectiveFrozenNames` for `writeViewportOverrides`, where
 *     `existingNames` (`vpEntity.frozenLayers`) is the CURRENT semantic truth and
 *     `frozenLayerHandles` is the STALE native-331 carrier that has not yet
 *     absorbed a freshly-VPLAYER-frozen name (VPLAYER intentionally defers handle
 *     synthesis to export — see `resolveFrozenLayerHandlesFromNames`). Here the
 *     native carrier must NEVER cause a subtraction: a name absent from the (stale)
 *     handle carrier is a NEW in-editor freeze, not a thaw (@codex #514 "Keep new
 *     semantic freezes when writing XDATA"). Only PASS B (union) applies, exactly
 *     as before the trustworthy-subtract PASS A was introduced.
 *
 * PASS A (import only) — carry over existing names, SUBTRACTING any the
 * trustworthy native 331 shows was THAWED:
 *   - SUBTRACT: an existing XDATA name whose LAYER handle is RESOLVABLE but ABSENT
 *     from the present native 331 was THAWED by a native writer (AutoCAD dropped the
 *     331 handle but left our proprietary XDATA name stale). Because the caller
 *     trusts the native 331 as consistent-by-construction, that name is DROPPED so
 *     it is not re-frozen / re-emitted as a fresh 331 on the next export (@codex
 *     #514 "Preserve native thaws when trusting v3 331 data"). This is symmetric
 *     with the legacy PRUNE path (`pruneStaleFrozenLayerHandles`), which drops the
 *     stale HANDLE; here the reconciled truth is the NAME list, so we drop the
 *     stale NAME. The native carrier is trusted EVEN WHEN IT IS NOW EMPTY — a v3+
 *     writer keeps 331 consistent with the XDATA names on every export, so a fully
 *     empty carrier positively means every layer was thawed, not "no evidence
 *     either way" (@codex #514 "Handle v3 thaws that remove every 331"; see the
 *     caller-side early-return fix in `resolveViewportFrozenLayerNames`).
 *   - An existing name is KEPT (never treated as a thaw) when it has NO resolvable
 *     LAYER handle — a handle-less or dangling name cannot be represented in 331 and
 *     round-trips ONLY via the XDATA fallback, so its absence from 331 is NOT thaw
 *     evidence (PRESERVE-SOURCE, NEVER-INFER).
 *   - When `trustNativeAsAuthoritative` is false/omitted, PASS A never subtracts —
 *     every existing name is preserved verbatim regardless of the native carrier.
 *
 * PASS B (both directions) — union in the names resolved from the native 331
 * handles (a foreign per-VP freeze, an external freeze ADDED after our export, or
 * export-side surfacing of a handle-only native freeze into the XDATA carrier).
 * Already-listed names dedup case-insensitively; a dangling handle (no known LAYER)
 * contributes no name and survives verbatim on `frozenLayerHandles` upstream. A
 * no-op when the handle list is empty. Kept/added names retain their original
 * order/casing, so a plain round-trip of OUR own export (which carries the SAME set
 * in both the XDATA and the native 331) is order-stable and never double-lists a
 * layer.
 */
export function mergeFrozenLayerNamesFromHandles(
  existingNames: readonly string[],
  frozenLayerHandles: readonly string[] | undefined,
  layers: readonly FrozenLayerRef[],
  options?: {
    /**
     * The native group-331 carrier is a TRUSTWORTHY, COMPLETE picture of this
     * viewport's frozen-layer set — pass `true` ONLY from the import-side
     * trustworthy-native regime (see `resolveViewportFrozenLayerNames`). Default
     * `false` is required for the export call site (see function doc above).
     */
    trustNativeAsAuthoritative?: boolean;
  },
): string[] {
  const result: string[] = [];
  const seen = new Set<string>();
  const add = (name: string): void => {
    const key = name.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    result.push(name);
  };
  const handles = frozenLayerHandles ?? [];
  const trustNative = options?.trustNativeAsAuthoritative ?? false;
  const nativeHandleSet = new Set<string>();
  for (const handle of handles) nativeHandleSet.add(handle.toLowerCase());
  const nameToHandle = buildNameToHandle(layers);
  // PASS A — see function doc: subtracts stale thawed names ONLY when the caller
  // declares the native carrier trustworthy (import), never on the export path.
  for (const name of existingNames) {
    if (trustNative) {
      const handle = nameToHandle.get(name.toLowerCase());
      if (handle !== undefined && !nativeHandleSet.has(handle.toLowerCase())) {
        continue; // thawed by a native writer — drop the stale name
      }
    }
    add(name);
  }
  // PASS B — union in the names resolved from the native 331 handles. A no-op when
  // `handles` is empty (covers both "no native carrier at all" and "v3+ trustworthy
  // carrier thawed to zero" — PASS A above already handled the latter's subtraction).
  const handleToName = buildHandleToName(layers);
  for (const handle of handles) {
    const name = handleToName.get(handle.toLowerCase());
    if (name !== undefined) add(name);
  }
  return result;
}

/**
 * IMPORT (merge/foreign path) — the subset of a viewport's native group-331 handles
 * that DID NOT resolve to a known LAYER name (dangling / foreign handles).
 *
 * After `mergeFrozenLayerNamesFromHandles` folds every RESOLVABLE handle into the
 * semantic `frozenLayers` NAME list, that NAME is the single representation of the
 * freeze — the raw handle MUST be CLEARED, otherwise the store entity carries the
 * SAME freeze twice (once as a name, once as a handle). The agent-bridge projector
 * (`projectViewportFrozenLayers.ts` `frozenLayerRows`) and the `viewports` facet
 * concatenate/count `frozenLayers` + `frozenLayerHandles` as two PARALLEL raw
 * carriers with NO name↔handle dedup, so a retained resolvable handle surfaces a
 * single AutoCAD-authored freeze as BOTH a `{layerName}` and a `{handle}` row and
 * doubles the count exposed to agents (@codex #486 P2 "Avoid double-counting
 * resolved native freezes"). Clearing the resolved handles keeps the invariant
 * "native 331 without XDATA names → `frozenLayers` populated once, resolved handles
 * cleared".
 *
 * A DANGLING handle (resolves to NO known LAYER) has NO name representation, so it
 * MUST survive verbatim on `frozenLayerHandles` — dropping it would LOSE the freeze
 * entirely (PRESERVE-SOURCE, ПРИОРИТЕТ #1) and `resolveFrozenLayerHandlesFromNames`
 * re-emits it on export. Its projector row is a genuine DISTINCT freeze (no name
 * exists for it), not a double-count of a named one.
 */
export function retainUnresolvedFrozenLayerHandles(
  frozenLayerHandles: readonly string[] | undefined,
  layers: readonly FrozenLayerRef[],
): string[] {
  if (!frozenLayerHandles || frozenLayerHandles.length === 0) return [];
  const handleToName = buildHandleToName(layers);
  return frozenLayerHandles.filter(
    (handle) => handleToName.get(handle.toLowerCase()) === undefined,
  );
}

/**
 * IMPORT — prune STALE native group-331 handles for a viewport whose frozen-layer
 * set is authoritatively owned by a LEGACY (pre-`VP_OVERRIDES_CONSISTENT_NATIVE_331_
 * VERSION`) EDIT_DWG_VP_OVERRIDES XDATA (the `frozenKeys` NAME list). This runs ONLY
 * for legacy-authoritative viewports: a v3+ writer keeps its native 331 consistent,
 * so its viewports take the trustworthy-native MERGE path instead (see
 * `resolveViewportFrozenLayerNames` and `collectXdataAuthoritativeVpHandles`), which
 * preserves an externally-ADDED native freeze rather than pruning it (@codex #486 P2
 * "Do not discard native freezes added after our export"). A handle is kept iff either
 *   - it resolves to NO known LAYER (dangling / foreign) — preserved verbatim for
 *     byte-fidelity (we cannot claim a layer we don't know was thawed), or
 *   - it resolves to a LAYER whose NAME is still in the authoritative list, or
 *   - the authoritative list is itself STALE relative to the LAYER table (see below).
 * A handle whose LAYER exists but is NO LONGER in the authoritative name list is a
 * STALE freeze (an older exporter copied 331 verbatim after a VPLAYER thaw / layer
 * replacement) and is DROPPED — so it can never re-surface the thawed freeze on the
 * next export (which unions resolvable handle names back into the XDATA carrier).
 *
 * RENAME GUARD (@codex #486 P2 "preserve renamed-layer native freeze handles"): the
 * authoritative XDATA name list can only be TRUSTED to identify a thaw when the list
 * itself is CONSISTENT with the current LAYER table — every listed name still
 * resolves to a layer. If an authoritative name resolves to NO current layer, the
 * list is STALE: a foreign CAD tool renamed a frozen layer but left our proprietary
 * XDATA untouched, while the native 331 handle still points at the SAME (now-renamed)
 * LAYER record. That handle now resolves to the NEW name, which is absent from the
 * stale list — dropping it as "stale" would silently thaw a still-valid freeze. So
 * when the list has ANY dangling (unresolvable) name we PRESERVE resolvable handles
 * rather than infer a thaw from a name mismatch (PRESERVE-SOURCE, NEVER-INFER). Note
 * a genuine thaw's list stays fully resolvable (the thawed name is simply removed,
 * not renamed away), so the migration cleanup above is unaffected.
 *
 * See `mergeFrozenLayerNamesFromHandles` for the symmetric NON-authoritative path.
 */
export function pruneStaleFrozenLayerHandles(
  authoritativeNames: readonly string[],
  frozenLayerHandles: readonly string[] | undefined,
  layers: readonly FrozenLayerRef[],
): string[] {
  if (!frozenLayerHandles || frozenLayerHandles.length === 0) return [];
  const handleToName = buildHandleToName(layers);
  const nameKeys = new Set<string>();
  for (const name of authoritativeNames) nameKeys.add(name.toLowerCase());
  // Rename guard: is the authoritative list consistent with the current LAYER table?
  // A listed name that resolves to NO layer means a frozen layer was renamed away
  // (or otherwise diverged) and the list is STALE — do not trust it to prune.
  const layerNameKeys = new Set<string>();
  for (const layer of layers) layerNameKeys.add(layer.name.toLowerCase());
  const authoritativeListIsStale = authoritativeNames.some(
    (name) => !layerNameKeys.has(name.toLowerCase()),
  );
  return frozenLayerHandles.filter((handle) => {
    const name = handleToName.get(handle.toLowerCase());
    // Dangling → keep (foreign byte-fidelity, cannot claim a thaw).
    if (name === undefined) return true;
    // Still named in the authoritative list → keep.
    if (nameKeys.has(name.toLowerCase())) return true;
    // Resolvable but unlisted: a stale post-thaw handle to DROP — UNLESS the
    // authoritative list is itself stale (rename evidence), then PRESERVE.
    return authoritativeListIsStale;
  });
}

/**
 * IMPORT PASS — reconcile every viewport's NATIVE group-331 frozen-layer handles
 * against the semantic `frozenLayers` NAMES across all routed spaces, mutating the
 * viewport entities in place. THREE authority regimes:
 *
 *   - MERGE, TRUSTED (the native 331 is the trustworthy freeze source) — resolves
 *     the native handles to NAMES and unions onto `frozenLayers`
 *     (`mergeFrozenLayerNamesFromHandles` with `trustNativeAsAuthoritative: true`),
 *     then CLEARS the resolved handles so the freeze is represented ONCE
 *     (`retainUnresolvedFrozenLayerHandles` keeps only danglers — @codex #486 P2
 *     double-count fix). Applies when EITHER:
 *       (a) FOREIGN — no EDIT_DWG_VP_OVERRIDES block owning the freeze set (a plain
 *           AutoCAD file, or a scale/lock-only block): the native 331 is the ONLY
 *           source. Without the merge a foreign per-VP freeze imports EMPTY. Such a
 *           viewport ALWAYS decodes with EMPTY `frozenLayers` at `toDrawingEntity`
 *           time (no owning XDATA block means nothing overwrote the `[]` baseline —
 *           see `dxfBridge.drawingToDxf.ts`), so trusting the carrier is a no-op for
 *           it: PASS A has nothing to subtract from; OR
 *       (b) POSITIVELY v3+ TRUSTWORTHY-NATIVE — the viewport's OWN decoded XDATA
 *           carries `vpOverridesWriterVersion >= VP_OVERRIDES_CONSISTENT_NATIVE_331_
 *           VERSION` (so its 331 is consistent by construction — no stale post-thaw
 *           handle possible, EVEN WHEN THE CARRIER IS NOW EMPTY). A resolvable handle
 *           absent from the XDATA names is an EXTERNAL post-export ADD (a native
 *           writer edited 331, left our XDATA stale) and is PRESERVED by merging, not
 *           pruned (@codex #486 P2 "Do not discard native freezes added after our
 *           export"). SYMMETRICALLY, an XDATA name whose resolvable LAYER handle is
 *           ABSENT from the trustworthy 331 was THAWED by that native writer and is
 *           SUBTRACTED, so it is not re-frozen / re-emitted as a fresh 331 (@codex
 *           #514 "Preserve native thaws when trusting v3 331 data") — INCLUDING when
 *           AutoCAD thawed EVERY frozen layer, leaving the native 331 carrier fully
 *           empty (@codex #514 "Handle v3 thaws that remove every 331": the entry
 *           gate below no longer bails out before reconciliation runs just because
 *           `frozenLayerHandles` is empty — a v3+ writer's empty carrier is
 *           trustworthy zero, not "no evidence"). For a clean round-trip the handles
 *           already match the names, so the merge is a no-op.
 *     Checking `vpOverridesWriterVersion` DIRECTLY on the entity (set at its own
 *     `toDrawingEntity` decode time) — rather than only "not in the legacy-
 *     authoritative set" — is DELIBERATE (@codex #514 P2 "Don't trust native 331 for
 *     unclassified XDATA viewports"): the raw-document handle-keyed pre-scan
 *     (`collectXdataAuthoritativeVpHandles`) can only KEY a viewport it can match by
 *     native DXF `handle`; a LEGACY (pre-v3) viewport whose `handle` is absent is
 *     invisible to that pre-scan and would otherwise wrongly fall through to the
 *     TRUSTED branch by mere absence-of-evidence, silently thawing layers a genuine
 *     legacy PRUNE would have preserved.
 *
 *   - MERGE, UNTRUSTED (an XDATA-owned viewport with NEITHER a legacy-authoritative
 *     match NOR a positive v3 marker — e.g. the handle-less-legacy case above, or any
 *     other provenance gap) — same union PASS but `trustNativeAsAuthoritative: false`,
 *     so PASS A never subtracts: safe, conservative, ambiguous-provenance fallback,
 *     symmetric with the EXPORT-side default (see `mergeFrozenLayerNamesFromHandles`).
 *
 *   - PRUNE (legacy-authoritative) — our EDIT_DWG_VP_OVERRIDES owns the freeze set
 *     but the file is LEGACY (pre-consistent-331 writer, POSITIVELY matched by
 *     `xdataAuthoritativeHandles`). The XDATA NAME list is the reconciled truth and
 *     the native 331 may be a stale copy an older exporter left verbatim after a
 *     thaw. Native handles must NOT ADD names (that would re-freeze a thawed layer),
 *     so PRUNE any stale resolvable handle (`pruneStaleFrozenLayerHandles`). Nothing
 *     to prune when the carrier is already empty, so that case is skipped entirely.
 *
 * Every MERGE sub-case (TRUSTED or UNTRUSTED) first snapshots the FULL verbatim
 * native 331 (order + casing) onto `sourceFrozenLayerHandles` BEFORE
 * `retainUnresolvedFrozenLayerHandles` clears the resolved entries from
 * `frozenLayerHandles` — the double-count-safe clearing is otherwise LOSSY for the
 * next export's byte-order/casing fidelity (@codex #514 P2 "Preserve verbatim 331
 * order before clearing handles"; see `sourceFrozenLayerHandles` in `model.ts` and
 * its export-side consumer in `dxfBridge.entityConversion.ts`).
 *
 * Viewports with NEITHER a native handle NOR a semantic name are untouched, so a
 * drawing with no per-VP freeze round-trips byte-identically. A viewport is no
 * longer skipped merely because `frozenLayerHandles` is empty — a v3+
 * trustworthy-native viewport can carry a fully-thawed (empty) native carrier
 * alongside stale semantic names that still need reconciling (@codex #514).
 */
export function resolveViewportFrozenLayerNames(
  spaces: readonly { entities: readonly FrozenLayerViewport[] }[],
  layers: readonly FrozenLayerRef[],
  xdataAuthoritativeHandles?: ReadonlySet<string>,
): void {
  for (const space of spaces) {
    for (const entity of space.entities) {
      if (entity.type !== "Viewport") continue;
      const nativeHandles = entity.frozenLayerHandles;
      const hasHandles = nativeHandles !== undefined && nativeHandles.length > 0;
      const hasNames =
        entity.frozenLayers !== undefined && entity.frozenLayers.length > 0;
      // Nothing to reconcile in either direction — untouched, byte-identical.
      if (!hasHandles && !hasNames) continue;
      const legacyAuthoritative =
        xdataAuthoritativeHandles !== undefined &&
        entity.id !== undefined &&
        xdataAuthoritativeHandles.has(entity.id.toLowerCase());
      if (legacyAuthoritative) {
        // Legacy XDATA owns the freeze set — keep its names, prune stale native
        // handles (a v3+ trustworthy-native VP is excluded from this set and takes
        // the merge branch below). Nothing to prune when the carrier is empty.
        if (!hasHandles) continue;
        entity.frozenLayerHandles = pruneStaleFrozenLayerHandles(
          entity.frozenLayers ?? [],
          entity.frozenLayerHandles,
          layers,
        );
      } else {
        // FOREIGN, POSITIVELY v3+ trustworthy-native, or an UNCLASSIFIED XDATA-owned
        // viewport the raw-document pre-scan could not key — see function doc.
        //
        // Snapshot the FULL verbatim native 331 BEFORE clearing resolved handles, in
        // EVERY sub-case (TRUSTED or UNTRUSTED both clear via
        // `retainUnresolvedFrozenLayerHandles` below) — @codex #514 P2 "Preserve
        // verbatim 331 order before clearing handles".
        if (hasHandles) {
          entity.sourceFrozenLayerHandles = [...nativeHandles];
        }
        // POSITIVE gate (@codex #514 P2 "Don't trust native 331 for unclassified
        // XDATA viewports"): `!legacyAuthoritative` alone is NOT proof of v3+
        // trustworthiness — only subtract (trust) when EITHER there is nothing to
        // wrongly subtract (`!hasNames` — a genuinely foreign viewport, whose
        // `frozenLayers` always decodes empty with no owning XDATA block) OR the
        // entity's OWN decoded XDATA positively carries a v3+ writer marker. An
        // XDATA-owned-but-unclassified viewport (hasNames && not positively v3) falls
        // back to the same safe union-only behavior as the export path.
        const positivelyV3Trustworthy =
          entity.vpOverridesWriterVersion !== undefined &&
          entity.vpOverridesWriterVersion >=
            VP_OVERRIDES_CONSISTENT_NATIVE_331_VERSION;
        const trustNativeAsAuthoritative = !hasNames || positivelyV3Trustworthy;
        entity.frozenLayers = mergeFrozenLayerNamesFromHandles(
          entity.frozenLayers ?? [],
          entity.frozenLayerHandles,
          layers,
          { trustNativeAsAuthoritative },
        );
        entity.frozenLayerHandles = retainUnresolvedFrozenLayerHandles(
          entity.frozenLayerHandles,
          layers,
        );
        // @codex #514 P2 "Update stale XDATA after trusting native 331" — the raw
        // EDIT_DWG_VP_OVERRIDES XDATA block (when it owns the freeze set) still
        // carries the OLD, pre-reconciliation frozen-layer `1000` groups. ANY later
        // viewport command (lock/scale/VPLAYER) calls readViewportOverrides(vp),
        // which gives those STALE on-file groups precedence over the just-
        // reconciled entity.frozenLayers (`frozenLayerGroupsFound` short-circuits
        // the typed-field baseline) — silently REVERTING this reconciliation the
        // moment any other command touches the entity. Rebuild the XDATA block
        // with the reconciled name list, preserving scale/lock/ucsName verbatim,
        // so it stays a faithful mirror of the typed field. Gated on
        // `xdataOwnsFrozenLayerSet` so a genuinely FOREIGN viewport (no owning
        // block) never gets a new proprietary XDATA block injected pre-emptively.
        if (xdataOwnsFrozenLayerSet(entity.xdata)) {
          const currentOverrides = readViewportOverrides(entity);
          writeViewportOverrides(
            { ...currentOverrides, frozenLayers: entity.frozenLayers },
            entity,
          );
        }
      }
    }
  }
}

/**
 * EXPORT — build the group-331 handle list for a viewport from the NATIVE
 * frozen-layer handle carrier plus the semantic frozen-layer NAMES.
 *
 * PRESERVE-SOURCE, NEVER-INFER: the native `frozenLayerHandles` (group 331) ARE
 * the round-trip source and every one is re-emitted VERBATIM in its original
 * position — resolvable OR dangling. This function NEVER infers a "thaw" from a
 * name's absence: a thaw is represented POSITIVELY upstream (the VPLAYER command
 * removes the layer's handle from `frozenLayerHandles`, and a re-import whose
 * EDIT_DWG_VP_OVERRIDES XDATA is authoritative prunes any stale handle — see
 * `resolveViewportFrozenLayerNames`). This makes a pure round-trip byte-order-
 * identical (e.g. `[2F, DEAD, 30]` stays `[2F, DEAD, 30]`) AND preserves a
 * handle-only native freeze (a model that carries `frozenLayerHandles` but never
 * populated the semantic names) instead of silently dropping it.
 *
 * PASS 1 — re-emit every verbatim source handle in order (deduped).
 * PASS 2 — append the LAYER handle for any frozen NAME not already emitted (a
 *   fresh in-editor freeze with no source 331 slot). A name whose handle is
 *   already present dedups away; a frozen NAME that resolves to no LAYER handle
 *   (e.g. a from-scratch layer with no table handle) contributes no 331 — its
 *   freeze still round-trips through the EDIT_DWG_VP_OVERRIDES XDATA fallback.
 */
export function resolveFrozenLayerHandlesFromNames(
  frozenNames: readonly string[],
  verbatimHandles: readonly string[] | undefined,
  layers: readonly FrozenLayerRef[],
): string[] {
  const nameToHandle = buildNameToHandle(layers);
  const result: string[] = [];
  const seen = new Set<string>();
  const addHandle = (handle: string): void => {
    const key = handle.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    result.push(handle);
  };
  // PASS 1 — preserve every verbatim source handle in its original 331 order
  // (native carrier is the source of truth; thaw is never inferred here).
  if (verbatimHandles) {
    for (const handle of verbatimHandles) addHandle(handle);
  }
  // PASS 2 — append NEWLY frozen names (in-editor freeze with no source 331
  // position). A name already emitted via its verbatim handle above dedups away.
  for (const name of frozenNames) {
    const handle = nameToHandle.get(name.toLowerCase());
    if (handle !== undefined) addHandle(handle);
  }
  return result;
}
